/**
 * Voxel shader materials (GLSL ES 3.0).
 *
 * All three passes share one shader with different settings:
 *  - opaque:      plain, backface culled
 *  - cutout:      alpha-tested (leaves, glass, plants), double-sided
 *  - translucent: blended liquids, sampled from the animated liquid array
 * plus two depth-only variants used as sun shadow casters.
 *
 * Vertex attributes are the mesher's packed format (see mesher/greedy.ts):
 *   apos   = (local xyz + 1) * 256, tile
 *   auv    = tile-space uv * 16, sky | block << 4 | meta << 8
 *   acolor = sRGB tint, ambient occlusion
 *
 * Lighting is computed in linear HDR: sky ambient scaled by the voxel
 * skylight, direct sun/moon light (shadow-mapped where enabled), and warm
 * block light. PostFX tonemaps the result.
 *
 * The atlas tile is sampled with textureGrad() so mip selection stays correct
 * even though fract() introduces a discontinuity at tile repeat boundaries.
 */

import * as THREE from 'three';
import { ATLAS_COLS, ATLAS_H, ATLAS_W, CELL, GUTTER, TILE, tileOriginU, tileOriginV } from '../atlasLayout';
import { META_EMISSIVE, META_FIRE, META_SHIFT, META_SWAY, META_WAVE, POS_SCALE, UV_SCALE } from '../mesher/greedy';
import type { Quality } from '../settings';
import { MASK_ALPHA } from './atlasOverlays';
import type { EnvUniforms } from './env';
import { LIGHTMAP_GLSL } from './lightmap';
import type { LiquidTextures } from './liquid';
import type { FireTextures } from './fire';
import { SHADOW_GLSL, type ShadowUniforms } from './shadows';
import { T as TILES, COLOR_TILES } from '../blocks';
const T_PORTAL = TILES.NETHER_PORTAL;
const T_SOUL_FIRE = TILES.SOUL_FIRE;

/** Number -> GLSL float literal. */
const f = (n: number): string => (Number.isInteger(n) ? n.toFixed(1) : String(n));

/** Vanilla fire texture animation: one frame per game tick (20 fps). */
const FIRE_FPS = 20;

/** Tiles (flat atlas indices) that hold rain puddles: hard man-made / bare-earth surfaces. */
const PUDDLE_NAMES = ['STONE', 'COBBLE', 'DIRT', 'COARSE_DIRT', 'GRAVEL', 'STONE_BRICK', 'BRICKS', 'MOSSY_COBBLE',
  'SMOOTH_STONE', 'DIRT_PATH_TOP', 'COBBLED_DEEPSLATE', 'POLISHED_DEEPSLATE', 'DEEPSLATE_BRICKS', 'DEEPSLATE_TILES',
  'PLANKS', 'SPRUCE_PLANKS', 'BIRCH_PLANKS', 'JUNGLE_PLANKS', 'ACACIA_PLANKS', 'DARK_OAK_PLANKS', 'MANGROVE_PLANKS',
  'CHERRY_PLANKS', 'PALE_OAK_PLANKS', 'PACKED_MUD', 'MUD_BRICKS'];
const PUDDLE_TILE_LIST: number[] = [
  ...PUDDLE_NAMES.map((n) => (TILES as Record<string, number>)[n]).filter((v) => typeof v === 'number'),
  ...Object.keys(COLOR_TILES).filter((k) => k.endsWith('_concrete')).map((k) => COLOR_TILES[k]),
].slice(0, 64);
const PUDDLE_COUNT = PUDDLE_TILE_LIST.length;
const PUDDLE_TILES = Float32Array.from({ length: 64 }, (_, i) => PUDDLE_TILE_LIST[i] ?? -1);

const DECODE = /* glsl */ `
in vec4 apos;
in vec4 auv;
in vec4 acolor;

const float LIGHT_RANGE = ${f(1 << META_SHIFT)};

vec3 localPos() { return apos.xyz / ${f(POS_SCALE)} - 1.0; }
// atlas-space uv of a tile's content origin
vec2 tileOrigin(float tile) {
  float row = floor(tile / ${f(ATLAS_COLS)});
  float col = tile - row * ${f(ATLAS_COLS)};
  return (vec2(col, row) * ${f(CELL)} + ${f(GUTTER)}) / vec2(${f(ATLAS_W)}, ${f(ATLAS_H)});
}
`;

const VERT = /* glsl */ `
${DECODE}
uniform float uTime;

out vec2 vTileUv;
flat out vec2 vOrigin;
out vec3 vTint;
out float vAo;
out vec2 vLight;
out highp vec3 vWorldPos;
flat out int vMeta;
flat out float vMaskTint;

void main() {
  float lightMeta = auv.z + 0.5;
  float metaF = floor(lightMeta / LIGHT_RANGE);
  float lightF = floor(lightMeta) - metaF * LIGHT_RANGE;
  float tile = floor(apos.w + 0.5);
  int meta = int(metaF);

  vec4 wp = modelMatrix * vec4(localPos(), 1.0);
  if ((meta & ${META_WAVE}) != 0) {
    wp.y += sin(uTime * 1.7 + wp.x * 0.6 + wp.z * 0.8) * 0.04 - 0.02;
  }
#ifdef SWAY
  if ((meta & ${META_SWAY}) != 0) {
    // displacement is a pure function of world position, so shared vertices
    // of neighbouring quads move together and no seams open
    float phase = uTime * 1.4 + wp.x * 0.7 + wp.z * 0.9 + wp.y * 0.4;
    wp.x += sin(phase) * 0.045;
    wp.z += cos(phase * 0.8 + 1.7) * 0.045;
  }
#endif
  vWorldPos = wp.xyz;
  gl_Position = projectionMatrix * viewMatrix * wp;

  vTileUv = auv.xy / ${f(UV_SCALE)};
  vOrigin = tileOrigin(tile);
  // tint is authored in sRGB; lighting happens in linear
  vTint = pow(acolor.rgb, vec3(2.2));
  vAo = acolor.a;
  float blk = floor(lightF / 16.0);
  vLight = vec2(lightF - blk * 16.0, blk) / 15.0;
  vMeta = meta;
  vMaskTint = auv.w;
}
`;

const FRAG = /* glsl */ `
precision highp float;
precision highp sampler2DArray;

#ifdef LIQUID
uniform sampler2DArray uFire;   // nether portal panes (sheet 2), see fire.ts
uniform vec4 uFireFrames;
uniform sampler2DArray uLiquid;
uniform vec3 uLiquidFrames;  // water frame count, lava start layer, lava frame count
#else
uniform sampler2D uAtlas;
/** Animated fire sheets: layers are [fire_0 frames..., fire_1 frames...].
 *  uFireFrames = (frames per sheet, sheet count, unused, unused). */
uniform sampler2DArray uFire;
uniform vec4 uFireFrames;
#endif
uniform float uAlphaTest;
uniform float uOpacity;
uniform float uTime;

uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uTrueSunDir;
uniform vec3 uAmbientColor;
uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform vec3 uSunGlow;
uniform float uGamma;
uniform vec3 uDimAmbient;
uniform float uWetness;
uniform float uPuddleTiles[64];
uniform int uPuddleCount;

in vec2 vTileUv;
flat in vec2 vOrigin;
in vec3 vTint;
in float vAo;
in vec2 vLight;
in highp vec3 vWorldPos;
flat in int vMeta;
flat in float vMaskTint;

// GLSL3: three.js only defines gl_FragColor for GLSL1 shaders — declare our own output.
out vec4 fragColor;

${SHADOW_GLSL}

const vec2 TILE_UV = vec2(${f(TILE / ATLAS_W)}, ${f(TILE / ATLAS_H)});
// Scene-referred scale of the env sun colour so a sunlit top face lands near
// albedo * 1.0 before tonemapping.
const float SUN_SCALE = 0.30;
const float AMBIENT_SCALE = 0.62;
// luminance of sky.ts AMBIENT_DAY: full daylight = no sky darkening
const float AMBIENT_DAY_LUM = 0.748;
// torch light: warm, and bright enough to read against daylight ambient
const vec3 BLOCK_LIGHT = vec3(1.75, 1.5, 1.2);


const vec3 NORMALS[7] = vec3[7](
  vec3(1.0, 0.0, 0.0), vec3(-1.0, 0.0, 0.0),
  vec3(0.0, 1.0, 0.0), vec3(0.0, -1.0, 0.0),
  vec3(0.0, 0.0, 1.0), vec3(0.0, 0.0, -1.0),
  vec3(0.0, 1.0, 0.0)
);
// Vanilla directional face shade (+X,-X,+Y,-Y,+Z,-Z, cross), applied to ambient
// so shaded sides still read as distinct planes.
const float FACE_SHADE[7] = float[7](0.68, 0.68, 1.0, 0.55, 0.84, 0.84, 1.0);

// Vanilla lightmap: level curve + Brightness (gamma) + warm block-light tint.
${LIGHTMAP_GLSL}

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 u = fract(p);
  u = u * u * (3.0 - 2.0 * u);
  return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
             mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x), u.y);
}
float rippleHeight(vec2 p) {
  return valueNoise(p * 0.9 + uTime * vec2(0.35, 0.22)) * 0.6
       + valueNoise(p * 2.3 - uTime * vec2(0.28, 0.41)) * 0.4;
}

vec3 skyColor(vec3 dir) {
  return mix(uHorizon, uZenith, pow(clamp(dir.y, 0.0, 1.0), 0.6));
}

// Rain puddles: tiles that can hold water (uPuddleTiles). Tile index from the flat atlas origin.
bool holdsWater(vec2 origin) {
  vec2 rc = floor(origin * vec2(${f(ATLAS_W)}, ${f(ATLAS_H)}) / ${f(CELL)});
  float tile = rc.y * ${f(ATLAS_COLS)} + rc.x;
  for (int i = 0; i < 64; i++) {
    if (i >= uPuddleCount) break;
    if (abs(uPuddleTiles[i] - tile) < 0.5) return true;
  }
  return false;
}

void main() {
  int nid = vMeta & 7;
#ifndef LIQUID
  // Cube and model faces (ids 0..5) bound closed volumes, so their back sides
  // are never needed — and the cutout pass is double-sided for the plant
  // cards. Left in, the back of a door / glass / leaf face is coplanar with the
  // neighbouring block's face on the same cell boundary and the two z-fight.
  // Leaves are the exception: the mesher emits one face per leaf-leaf boundary,
  // so its back side is the only thing filling the canopy from the other
  // direction. Keep it and shade it as the opposite face.
  if (!gl_FrontFacing && nid < 6) {
    if ((vMeta & ${META_SWAY}) == 0) discard;
    nid ^= 1;
  }
#endif
  if (nid == 7) nid = 2; // two-sided ground cover: shaded as a top face
  bool emissive = (vMeta & ${META_EMISSIVE}) != 0;
  bool isFire = false;

#ifdef LIQUID
  float ft = uTime * 10.0;
  vec4 tex;
  bool portal = (vMeta & ${META_FIRE}) != 0;
  if (portal) {
    // nether portal pane: the animated swirl, see-through (vanilla translucent layer)
    float frame = mod(floor(uTime * ${f(FIRE_FPS)}), uFireFrames.x);
    tex = texture(uFire, vec3(fract(vTileUv), 2.0 * uFireFrames.x + frame));
    isFire = true;
  } else if (emissive) {
    // lava: blend consecutive frames (vanilla interpolates it), slower flow
    float lt = ft * 0.5;
    float l0 = mod(floor(lt), uLiquidFrames.z);
    float l1 = mod(l0 + 1.0, uLiquidFrames.z);
    tex = mix(texture(uLiquid, vec3(vTileUv, uLiquidFrames.y + l0)),
              texture(uLiquid, vec3(vTileUv, uLiquidFrames.y + l1)), fract(lt));
  } else {
    vec2 luv = vTileUv;
    if (vMaskTint > 0.5) {
      // flowing water: scroll the texture along the flow (direction packed by the mesher)
      float ang = (vMaskTint - 1.0) * ${f((Math.PI * 2) / 256)};
      luv -= vec2(cos(ang), sin(ang)) * (uTime * 1.25); // the tile repeats, so this stays continuous
    }
    tex = texture(uLiquid, vec3(luv, mod(floor(ft), uLiquidFrames.x)));
  }
  // water is see-through; lava is a solid glowing surface
  float alpha = portal ? tex.a : emissive ? 1.0 : tex.a * uOpacity;
#else
  vec2 tf = fract(vTileUv);
  // Fire animates through its own frame array: vanilla's fire_0/fire_1 sheets
  // are 32-frame vertical strips played at one frame per game tick (20 fps),
  // and each fire block picks one of the two sheets — here from its world
  // position, the way the blockstate model is picked per block.
  isFire = (vMeta & ${META_FIRE}) != 0;
  vec2 cell = vec2(TILE_UV);
  // sample origin plus its screen-space derivatives: the mip-aware alpha test
  // below reads du/dv to size the texel footprint.
  vec2 uv, du, dv;
  vec4 tex;
  if (isFire) {
    // vWorldPos, not wp: wp is a vertex-stage local and is not visible here.
    float sheet = step(0.5, hash21(floor(vWorldPos.xz)));
    // nether portal panes (emissive + fire flags, portal tile) play sheet 2
    if (distance(vOrigin, vec2(${f(tileOriginU(T_PORTAL))}, ${f(tileOriginV(T_PORTAL))})) < 1e-5 && uFireFrames.y > 2.5) sheet = 2.0;
    // soul fire plays sheets 3/4 (soul_fire_0/1)
    if (distance(vOrigin, vec2(${f(tileOriginU(T_SOUL_FIRE))}, ${f(tileOriginV(T_SOUL_FIRE))})) < 1e-5 && uFireFrames.y > 4.5) sheet += 3.0;
    float frame = mod(floor(uTime * ${f(FIRE_FPS)}), uFireFrames.x);
    tex = texture(uFire, vec3(vTileUv, sheet * uFireFrames.x + frame));
    du = dFdx(vTileUv);
    dv = dFdy(vTileUv);
  } else {
    // Sample the tile content exactly; linear/mip filtering spreads into the
    // tile's own wrapped gutter, never into a neighbour's cell.
    uv = vOrigin + tf * cell;
    du = dFdx(vTileUv) * cell;
    dv = dFdy(vTileUv) * cell;
    tex = textureGrad(uAtlas, uv, du, dv);
  }
  float alpha = 1.0;
  if (uAlphaTest > 0.0) {
    // Mip-aware alpha test: at high mip levels a texel averages many source
    // pixels (foliage gaps averaged with leaves), so a fixed threshold makes
    // mid-alpha texels pop in/out as transparent speckles. Scale the threshold
    // down with the texel footprint so cutout surfaces stay coherent at range.
    // Fire is exempt: it is sampled unmipped (like vanilla's cutout layer), so
    // there is nothing to compensate for, and a lowered threshold would keep
    // the faint texels that make a grazing flame plane read as a bright streak.
    float footprint = isFire ? 1.0 : clamp(max(length(du), length(dv)) * ${f(ATLAS_W)}, 1.0, 32.0);
    float cutoff = uAlphaTest / sqrt(footprint);
#ifdef A2C
    // alpha-to-coverage: sharpen the edge to ~1px so MSAA antialiases it
    alpha = clamp((tex.a - cutoff) / max(fwidth(tex.a), 0.0001) + 0.5, 0.0, 1.0);
    if (alpha < 0.02) discard;
#else
    if (tex.a < cutoff) discard;
#endif
  }
#endif

  vec3 tint = vTint;
#ifndef LIQUID
  // grass block sides: only the marker-alpha texels (the grass strip) take the biome tint
  if (vMaskTint > 0.5) tint = mix(vec3(1.0), vTint, clamp((1.0 - tex.a) * ${f(255 / (255 - MASK_ALPHA))}, 0.0, 1.0));
#endif
  vec3 albedo = tex.rgb * tint;
  vec3 N = NORMALS[nid];
  vec3 toCam = cameraPosition - vWorldPos;
  float dist = length(toCam);
  vec3 V = toCam / max(dist, 0.0001);

  float skyB = lightCurve(vLight.x);
  float blkB = lightCurve(vLight.y);

  // Direct light only reaches cells the sky can see; this is also the whole
  // shadow term when shadow maps are off or out of range.
  float skyVis = smoothstep(0.55, 0.95, vLight.x);
  // cross plants have no real face normal: light them like the ground they stand on
  float ndl = max(dot(N, uSunDir), 0.0);
  float shadow = 1.0;
#if defined(SHADOW_CASCADES) && SHADOW_CASCADES > 0
  if (ndl > 0.0 && skyVis > 0.0) shadow = sampleShadow(vWorldPos, N, dist);
#endif
  float sun = ndl * shadow * skyVis;

  // vanilla sky darkening happens BEFORE the Brightness curve: split the
  // ambient uniform into a level (relative to full day) and a hue
  float ambL = dot(uAmbientColor, vec3(0.2126, 0.7152, 0.0722));
  float darken = clamp(ambL / AMBIENT_DAY_LUM, 0.0, 1.0);
  vec3 ambHue = uAmbientColor / max(darken, 1e-4);
  skyB = lightGamma(vLight.x / (4.0 - 3.0 * vLight.x) * darken);
  vec3 light = ambHue * (AMBIENT_SCALE * skyB * FACE_SHADE[nid])
             + uSunColor * (SUN_SCALE * sun)
             + BLOCK_LIGHT * blockLightTint(blkB) * FACE_SHADE[nid]
             + uDimAmbient * FACE_SHADE[nid]; // nether ambient light (world/dimension.ts)
  // (no constant floor here: the lightmap floor in render/lightmap.ts keeps light 0 from going black)
  vec3 color = albedo * light * vAo;
#ifndef LIQUID
  // rain: exposed ground darkens and desaturates a touch; flat water-holding blocks grow pixel puddles
  float puddle = 0.0;
  if (uWetness > 0.001 && nid != 3 && nid != 6) {
    float exposed = smoothstep(0.9, 1.0, vLight.x) * uWetness;
    float topF = nid == 2 ? 1.0 : (nid == 0 || nid == 1 || nid == 4 || nid == 5) ? 0.5 : 0.0;
    float wetK = exposed * topF;
    float lum = dot(albedo, vec3(0.2126, 0.7152, 0.0722));
    color = mix(color, vec3(lum) * light * vAo, 0.12 * wetK);
    color *= 1.0 - 0.15 * wetK;
#ifdef WET_FX
    if (nid == 2 && exposed > 0.001 && holdsWater(vOrigin)) {
      vec2 tx = floor(vWorldPos.xz * 16.0);  // texel grid: stays pixel-art
      vec2 wq = (tx + 0.5) / 16.0;
      float n = valueNoise(wq * 0.22) * 0.65 + valueNoise(wq * 0.47 + 17.3) * 0.35;
      float thr = mix(1.0, 0.69, exposed);
      puddle = step(thr, n);
      if (puddle > 0.0) {
        color *= 0.78;
        vec3 R = vec3(-V.x, abs(V.y), -V.z);
        float fres = min(0.04 + 0.96 * pow(1.0 - clamp(V.y, 0.0, 1.0), 5.0), 0.3);
        vec3 refl = skyColor(R) * mix(0.25, 1.0, skyB) * 0.55;
        float glint = pow(max(dot(R, uSunDir), 0.0), 200.0);
        color = mix(color, refl, fres) + uSunColor * (glint * 0.15 * skyVis * shadow);
        // ripples: 8-texel cells, a ring in about half of them, expanding on a texel grid
        vec2 cellId = floor(tx / 8.0);
        vec2 hh = vec2(hash21(cellId + 3.1), hash21(cellId + 9.7));
        float on = step(0.5, hash21(cellId + 21.3));
        vec2 ctr = floor(vec2(2.0) + hh * 4.0) + 0.5;
        float ph = fract(uTime * 0.7 + hash21(cellId + 5.5));
        float rr = ph * 3.5;
        float dd = length(tx - cellId * 8.0 - ctr);
        float ring = on * step(abs(dd - rr), 0.6) * (1.0 - ph);
        color += refl * 0.35 * ring * exposed;
      }
    }
#endif
  }
#endif
  if (isFire) {
    // Vanilla draws fire unlit but fully bright: the sheet already carries the
    // flame's contrast (white-hot core, deep red edges, black smoke), so a
    // large multiplier here only washes it into a flat orange lamp.
    color = albedo * 1.15;
  } else if (emissive) {
    color = albedo * (1.6 + 1.2 * dot(tex.rgb, vec3(0.33)));
  }

#if defined(LIQUID) && defined(WATER_FX)
  if (!emissive && nid == 2 && gl_FrontFacing) {
    // ripple normal from a scrolling height field (finite differences)
    vec2 p = vWorldPos.xz;
    float h0 = rippleHeight(p);
    vec3 wn = normalize(vec3(h0 - rippleHeight(p + vec2(0.35, 0.0)), 5.0, h0 - rippleHeight(p + vec2(0.0, 0.35))));
    // like vanilla, the water keeps its own colour at every view angle: the
    // sky reflection is a sheen on top, never a full mirror (that flattens
    // the whole ocean into one sky-blue sheet at grazing angles)
    float fres = 0.02 + 0.98 * pow(1.0 - max(dot(wn, V), 0.0), 5.0);
    vec3 R = reflect(-V, wn);
    R.y = abs(R.y);
    vec3 refl = skyColor(R) * mix(0.25, 1.0, skyB);
    float glint = pow(max(dot(R, uSunDir), 0.0), 320.0);
    // kept faint: vanilla water is a flat tinted texture, so the sheen and
    // glint only hint at a surface and the block texture stays readable
    color = mix(color, refl, fres * 0.2) + uSunColor * (glint * 0.5 * shadow * skyVis);
    alpha = mix(alpha, 0.9, fres * 0.25);
  }
#endif

  // Radial (cylindrical) fog: unlike view-depth fog it does not shift as the
  // camera turns, and it always hides the edge of the loaded chunk disc.
  float fogDist = max(length(toCam.xz), abs(toCam.y) * 0.7);
  float fog = smoothstep(uFogNear, uFogFar, fogDist);
  vec2 sunXZ = normalize(uTrueSunDir.xz + vec2(0.00001));
  float toward = max(dot(normalize(-toCam.xz + vec2(0.00001)), sunXZ), 0.0);
  vec3 fogColor = uFogColor + uSunGlow * (pow(toward, 4.0) + 0.12);
  color = mix(color, fogColor, fog);

  fragColor = vec4(color, alpha);
}
`;

const DEPTH_VERT = /* glsl */ `
${DECODE}
out vec2 vTileUv;
flat out vec2 vOrigin;
flat out int vMeta;

void main() {
  float tile = floor(apos.w + 0.5);
  vMeta = int(floor((auv.z + 0.5) / LIGHT_RANGE));
  vTileUv = auv.xy / ${f(UV_SCALE)};
  vOrigin = tileOrigin(tile);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(localPos(), 1.0);
  // pancake casters that poke out the near side of the light volume
  gl_Position.z = max(gl_Position.z, -gl_Position.w);
}
`;

const DEPTH_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uAtlas;
in vec2 vTileUv;
flat in vec2 vOrigin;
flat in int vMeta;
out vec4 fragColor;

void main() {
  // fire is a light source, not a caster: vanilla excludes it from shadows,
  // and its planes would otherwise drop a solid quad of shade
  if ((vMeta & ${META_FIRE}) != 0) discard;
#ifdef CUTOUT
  vec2 uv = vOrigin + fract(vTileUv) * vec2(${f(TILE / ATLAS_W)}, ${f(TILE / ATLAS_H)});
  if (textureLod(uAtlas, uv, 0.0).a < 0.5) discard;
#endif
  fragColor = vec4(1.0);
}
`;

export interface VoxelMaterials {
  opaque: THREE.ShaderMaterial;
  cutout: THREE.ShaderMaterial;
  translucent: THREE.ShaderMaterial;
  depthOpaque: THREE.ShaderMaterial;
  depthCutout: THREE.ShaderMaterial;
  /** Recompile for a quality preset (shadows, sway, water effects, MSAA). */
  setQuality(q: Quality): void;
}

export function createVoxelMaterials(
  atlas: THREE.Texture,
  liquid: LiquidTextures,
  fire: FireTextures,
  env: EnvUniforms,
  shadow: ShadowUniforms,
  quality: Quality,
): VoxelMaterials {
  // Env + shadow uniform objects are shared by reference, so the sky and the
  // shadow pass update every material at once.
  const shared = {
    ...env,
    ...shadow,
    uAtlas: { value: atlas },
    uFire: { value: fire.texture },
    uFireFrames: { value: new THREE.Vector4(fire.frames, fire.sheets, 0, 0) },
    uPuddleTiles: { value: PUDDLE_TILES },
    uPuddleCount: { value: PUDDLE_COUNT },
  };
  const base = { vertexShader: VERT, fragmentShader: FRAG, glslVersion: THREE.GLSL3 };

  const opaque = new THREE.ShaderMaterial({
    ...base,
    uniforms: { ...shared, uAlphaTest: { value: 0.0 }, uOpacity: { value: 1.0 } },
  });

  const cutout = new THREE.ShaderMaterial({
    ...base,
    uniforms: { ...shared, uAlphaTest: { value: 0.5 }, uOpacity: { value: 1.0 } },
    side: THREE.DoubleSide,
  });

  const translucent = new THREE.ShaderMaterial({
    ...base,
    uniforms: {
      ...shared,
      uAlphaTest: { value: 0.0 },
      uOpacity: { value: 0.9 },
      uLiquid: { value: liquid.texture },
      uLiquidFrames: { value: new THREE.Vector3(liquid.waterFrames, liquid.lavaStart, liquid.lavaFrames) },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const depthBase = {
    vertexShader: DEPTH_VERT,
    fragmentShader: DEPTH_FRAG,
    glslVersion: THREE.GLSL3,
    colorWrite: false,
    side: THREE.DoubleSide,
  };
  const depthOpaque = new THREE.ShaderMaterial({ ...depthBase, uniforms: { uAtlas: { value: atlas } } });
  const depthCutout = new THREE.ShaderMaterial({
    ...depthBase, uniforms: { uAtlas: { value: atlas } }, defines: { CUTOUT: 1 },
  });

  const setQuality = (q: Quality): void => {
    for (const m of [opaque, cutout, translucent]) {
      const d: Record<string, number> = { SHADOW_CASCADES: q.shadowCascades };
      if (m === translucent) d.LIQUID = 1;
      if (q.sway) d.SWAY = 1;
      if (q.waterFx) { d.WATER_FX = 1; d.WET_FX = 1; }
      if (q.msaa > 0 && m === cutout) d.A2C = 1;
      m.defines = d;
      m.needsUpdate = true;
    }
    cutout.alphaToCoverage = q.msaa > 0;
  };
  setQuality(quality);

  return { opaque, cutout, translucent, depthOpaque, depthCutout, setQuality };
}
